import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { FolderKanban, Calendar, CheckCircle2, Clock, Target, ChevronRight, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDetails, setProjectDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/projects`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectDetails = async (projectId) => {
    setLoadingDetails(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProjectDetails(data);
      }
    } catch (error) {
      console.error('Failed to load project details:', error);
      toast.error('Failed to load project details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSelectProject = (project) => {
    setSelectedProject(project);
    // Use id_string to avoid JavaScript integer precision issues with large Zoho IDs
    const projectId = project.id_string || String(project.id);
    fetchProjectDetails(projectId);
  };

  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'active' || statusLower === 'in progress') return 'text-primary bg-primary/10';
    if (statusLower === 'completed' || statusLower === 'closed') return 'text-green-500 bg-green-500/10';
    if (statusLower === 'on hold' || statusLower === 'paused') return 'text-yellow-500 bg-yellow-500/10';
    return 'text-muted-foreground bg-muted';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Recursive function to render tasks and all nested subtasks
  const renderTaskList = (tasks, depth = 0) => {
    if (!tasks || tasks.length === 0) return null;
    
    const sortedTasks = [...tasks].sort((a, b) => {
      const idA = a.id_string || String(a.id || 0);
      const idB = b.id_string || String(b.id || 0);
      return idA.localeCompare(idB);
    });

    return sortedTasks.map((task, idx) => {
      const taskId = task.id_string || task.id || idx;
      const hasSubtasks = task.subtask_list && task.subtask_list.length > 0;
      const marginLeft = depth * 24; // 24px per level
      const bgClass = depth === 0 ? 'bg-background' : depth === 1 ? 'bg-muted/50' : 'bg-muted/30';
      const borderClass = depth === 0 ? 'border-border' : 'border-border/50';
      
      return (
        <div key={taskId}>
          <div 
            className={`p-3 ${bgClass} rounded-md border ${borderClass}`}
            style={{ marginLeft: `${marginLeft}px` }}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                {depth > 0 && (
                  <span className="font-mono text-xs text-muted-foreground mr-1">
                    {'↳'.repeat(Math.min(depth, 3))}
                  </span>
                )}
                <span className="font-mono text-sm">{task.name}</span>
                {hasSubtasks && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({task.subtask_list.length} subtask{task.subtask_list.length > 1 ? 's' : ''})
                  </span>
                )}
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs font-mono whitespace-nowrap ${getStatusColor(task.status?.name || task.status)}`}>
                {task.status?.name || task.status || 'Open'}
              </span>
            </div>
          </div>
          {/* Render nested subtasks recursively */}
          {hasSubtasks && (
            <div className="mt-1 space-y-1">
              {renderTaskList(task.subtask_list, depth + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <DashboardLayout title="Projects">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Projects">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <FolderKanban className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-mono font-bold mb-2">Your Projects</h2>
              <p className="text-sm text-muted-foreground">
                View your active projects and track progress in real-time.
              </p>
            </div>
          </div>
        </div>

        {projects.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <FolderKanban className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-mono font-bold mb-2">No Projects Assigned</h3>
            <p className="text-muted-foreground">
              You don&apos;t have any projects assigned to your company yet. Contact your account manager for more information.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Project List */}
            <div className="lg:col-span-1 space-y-3">
              <h3 className="text-sm font-mono font-bold text-muted-foreground uppercase tracking-wider">
                All Projects ({projects.length})
              </h3>
              {projects.map((project) => {
                const projectId = project.id_string || String(project.id);
                const selectedId = selectedProject?.id_string || String(selectedProject?.id || '');
                return (
                <button
                  key={projectId}
                  onClick={() => handleSelectProject(project)}
                  className={`w-full text-left p-4 rounded-lg border transition-all ${
                    selectedId === projectId
                      ? 'bg-primary/10 border-primary'
                      : 'bg-card border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <h4 className="font-mono font-bold truncate pr-2">{project.name}</h4>
                    <ChevronRight className={`h-4 w-4 flex-shrink-0 transition-transform ${
                      selectedId === projectId ? 'rotate-90' : ''
                    }`} />
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-mono ${getStatusColor(project.status || project.status_name)}`}>
                      {project.status || project.status_name || 'Unknown'}
                    </span>
                  </div>
                  {project.owner_name && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Owner: {project.owner_name}
                    </p>
                  )}
                </button>
              );
              })}
            </div>

            {/* Project Details */}
            <div className="lg:col-span-2">
              {!selectedProject ? (
                <div className="bg-card border border-border rounded-lg p-12 text-center h-full flex flex-col items-center justify-center">
                  <FolderKanban className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Select a project to view details</p>
                </div>
              ) : loadingDetails ? (
                <div className="bg-card border border-border rounded-lg p-12 text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
                  <p className="text-muted-foreground mt-4">Loading project details...</p>
                </div>
              ) : projectDetails ? (
                <div className="space-y-4">
                  {/* Project Overview */}
                  <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-mono font-bold">{projectDetails.project?.name || selectedProject.name}</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {projectDetails.project?.description || 'No description available'}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-mono ${getStatusColor(projectDetails.project?.status || projectDetails.project?.status_name)}`}>
                        {projectDetails.project?.status || projectDetails.project?.status_name || 'Unknown'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-border">
                      <div>
                        <p className="text-xs text-muted-foreground">Start Date</p>
                        <p className="font-mono text-sm">{formatDate(projectDetails.project?.start_date)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">End Date</p>
                        <p className="font-mono text-sm">{formatDate(projectDetails.project?.end_date)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Owner</p>
                        <p className="font-mono text-sm">{projectDetails.project?.owner_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Tasks</p>
                        <p className="font-mono text-sm">
                          <span className="text-primary">{projectDetails.project?.task_stats?.completed || 0}</span>
                          <span className="text-muted-foreground"> / {projectDetails.project?.task_stats?.total || 0}</span>
                        </p>
                      </div>
                    </div>

                    {/* Progress Bar - Tasks Completed vs Outstanding */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-muted-foreground">Task Progress</span>
                        <span className="text-xs font-mono text-primary">
                          {projectDetails.project?.task_stats?.progress_percent || 0}% Complete
                        </span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-3">
                        <div 
                          className="bg-primary h-3 rounded-full transition-all"
                          style={{ width: `${projectDetails.project?.task_stats?.progress_percent || 0}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between mt-2 text-xs">
                        <span className="text-primary">
                          {projectDetails.project?.task_stats?.completed || 0} Completed
                        </span>
                        <span className="text-muted-foreground">
                          {projectDetails.project?.task_stats?.outstanding || 0} Outstanding
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Milestones */}
                  {projectDetails.milestones && projectDetails.milestones.length > 0 && (
                    <div className="bg-card border border-border rounded-lg p-6">
                      <h4 className="font-mono font-bold mb-4 flex items-center gap-2">
                        <Target className="h-5 w-5 text-accent" />
                        Milestones ({projectDetails.milestones.length})
                      </h4>
                      <div className="space-y-3">
                        {projectDetails.milestones.map((milestone, idx) => (
                          <div key={milestone.id || idx} className="p-3 bg-background rounded-md border border-border">
                            <div className="flex items-center justify-between">
                              <span className="font-mono text-sm">{milestone.name}</span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-mono ${getStatusColor(milestone.status || milestone.status_name)}`}>
                                {milestone.status || milestone.status_name || 'Pending'}
                              </span>
                            </div>
                            {milestone.end_date && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Due: {formatDate(milestone.end_date)}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tasks */}
                  {projectDetails.tasks && projectDetails.tasks.length > 0 && (
                    <div className="bg-card border border-border rounded-lg p-6">
                      <h4 className="font-mono font-bold mb-4 flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-primary" />
                        Tasks ({projectDetails.project?.task_stats?.total || projectDetails.tasks.length})
                      </h4>
                      <div className="space-y-2 max-h-[600px] overflow-y-auto">
                        {renderTaskList(projectDetails.tasks, 0)}
                      </div>
                    </div>
                  )}

                </div>
              ) : (
                <div className="bg-card border border-border rounded-lg p-12 text-center">
                  <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Could not load project details</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Projects;
